import { X, Download, Printer, Send, Shield, CheckCircle2, Clock, AlertTriangle, FileText, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function PackPreviewModal({ open, onClose, type = 'readiness', data = {} }) {
  if (!open) return null;

  const config = {
    readiness: { title: 'Readiness Dossier', subtitle: 'Stage readiness assessment pack', color: 'blue' },
    surveyor: { title: 'Surveyor-Ready Pack', subtitle: 'Inspection readiness submission', color: 'indigo' },
    buyer: { title: 'Buyer Handover Pack', subtitle: 'Settlement and handover documentation', color: 'emerald' },
    client: { title: 'Client Progress Summary', subtitle: 'Weekly project update', color: 'slate' },
  };
  const cfg = config[type] || config.readiness;

  return (
    <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="pack-preview-modal">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg bg-${cfg.color}-500/20 flex items-center justify-center`}>
              <FileText className={`w-4 h-4 text-${cfg.color}-400`} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">{cfg.title}</h3>
              <p className="text-[10px] text-slate-400">{cfg.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-8 gap-1.5 text-xs" onClick={() => toast.info('Print preview')}><Printer className="w-3.5 h-3.5" />Print</Button>
            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-8 gap-1.5 text-xs" onClick={() => toast.success('PDF exported')}><Download className="w-3.5 h-3.5" />Export</Button>
            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-8 gap-1.5 text-xs" onClick={() => toast.success('Sent to recipient')}><Send className="w-3.5 h-3.5" />Send</Button>
            <button onClick={onClose} className="text-slate-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* PDF body */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-6">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl mx-auto border border-slate-200" data-testid="pdf-preview-body">
            {/* Cover */}
            <div className="p-8 border-b border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-lg font-heading font-bold text-slate-900">easypermit</p>
                  <p className="text-xs text-slate-500">{cfg.title}</p>
                </div>
              </div>
              <h2 className="text-xl font-heading font-bold text-slate-900 mb-1">{data.projectName || '12 Smith Street Residence'}</h2>
              <p className="text-sm text-slate-500">{data.address || '12 Smith Street, Point Cook VIC 3030'}</p>
              <p className="text-xs text-slate-400 mt-2">
                Generated: {new Date().toLocaleDateString('en-AU')} · Permit: {data.permit || 'BP-2024-00847'} · {data.workspace || 'Oakline Custom Homes'}
              </p>
            </div>

            {/* Summary bar */}
            <div className="p-6 border-b border-slate-200 bg-slate-50/50">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Executive Summary</p>
              <div className="grid grid-cols-4 gap-3">
                {(data.stats || [
                  { label: 'Stage', value: 'Pre-Slab', status: 'info' },
                  { label: 'Readiness', value: data.readiness || '67%', status: data.readiness === '100%' ? 'success' : 'warning' },
                  { label: 'Blockers', value: data.blockers || '3', status: (data.blockers || 3) > 0 ? 'danger' : 'success' },
                  { label: 'Evidence', value: data.evidence || '4 files', status: 'info' },
                ]).map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase">{s.label}</p>
                    <p className={`text-lg font-heading font-bold ${s.status === 'danger' ? 'text-red-600' : s.status === 'success' ? 'text-emerald-600' : s.status === 'warning' ? 'text-amber-600' : 'text-slate-900'}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Conditions table */}
            <div className="p-6 border-b border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Conditions Status</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-slate-500 font-semibold">#</th>
                    <th className="text-left py-2 text-slate-500 font-semibold">Condition</th>
                    <th className="text-left py-2 text-slate-500 font-semibold">Owner</th>
                    <th className="text-left py-2 text-slate-500 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.conditions || [
                    { num: '2.1', title: 'Drainage Certificate', owner: 'D. Patel', status: 'cleared' },
                    { num: '2.2', title: 'Soil Report Verification', owner: 'D. Patel', status: 'blocker' },
                    { num: '2.3', title: 'Stormwater Connection', owner: 'S. Chen', status: 'blocker' },
                    { num: '2.4', title: 'Setback Confirmation', owner: 'M. Stevens', status: 'in-progress' },
                  ]).map((c, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 text-slate-400 font-mono">{c.num}</td>
                      <td className="py-2 text-slate-800 font-medium">{c.title}</td>
                      <td className="py-2 text-slate-600">{c.owner}</td>
                      <td className="py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${c.status === 'cleared' ? 'bg-emerald-50 text-emerald-700' : c.status === 'blocker' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                          {c.status === 'cleared' ? <CheckCircle2 className="w-2.5 h-2.5" /> : c.status === 'blocker' ? <AlertTriangle className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
                          {c.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Evidence */}
            <div className="p-6 border-b border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Evidence Pack</p>
              <div className="space-y-1.5">
                {(data.evidenceList || [
                  { file: 'Drainage_Certificate_v2.pdf', status: 'Approved', date: '18/01/2025' },
                  { file: 'Footing_Photo_01.jpg', status: 'Under review', date: '19/01/2025' },
                  { file: 'Site_Survey_Report.pdf', status: 'Approved', date: '15/01/2025' },
                ]).map((e, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <Eye className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="flex-1 text-slate-700">{e.file}</span>
                    <Badge className={`text-[9px] ${e.status === 'Approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{e.status}</Badge>
                    <span className="text-slate-400">{e.date}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sign-off strip */}
            <div className="p-6 bg-slate-50">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Review & Sign-off</p>
              <div className="grid grid-cols-3 gap-4">
                {['Prepared by', 'Reviewed by', 'Authorised by'].map((label, i) => (
                  <div key={i} className="border border-dashed border-slate-300 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-400 font-semibold">{label}</p>
                    <div className="h-8 mt-1" />
                    <Separator className="my-1" />
                    <p className="text-[10px] text-slate-400">Name / Date</p>
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-slate-400 text-center mt-4">
                This document is generated by easypermit for guidance only. Review all items before submission.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
